<template>
    <v-container fluid class="demo-container fill-height pa-0">

        <!-- Banner top -->
        <LicenzaBanner :licenza-stato="licenzaStato" :email-confirmata="emailConfirmata"
            @reinvia-email="reinviaEmail" />

        <!-- Layout due colonne: sidebar demo + contenuto bloccato -->
        <v-row class="fill-height ma-0" no-gutters>

            <!-- Sidebar: anteprima navigazione (disabilitata) -->
            <v-col cols="12" md="2" class="demo-sidebar d-none d-md-flex flex-column pa-3">
                <div class="sidebar-logo mb-4 d-flex align-center gap-2">
                    <v-icon color="primary" size="20">mdi-ruler-square</v-icon>
                    <span class="font-weight-bold text-body-2" style="color:#1E3A5F">Rilievi Misure</span>
                </div>

                <div v-for="voce in menuDemo" :key="voce.label" class="sidebar-item" :class="{ locked: voce.locked }">
                    <v-icon size="16" :color="voce.locked ? 'grey-lighten-1' : 'primary'" class="me-2">{{ voce.icon
                        }}</v-icon>
                    <span>{{ voce.label }}</span>
                    <v-icon v-if="voce.locked" size="12" color="grey-lighten-1" class="ms-auto">mdi-lock</v-icon>
                </div>
            </v-col>

            <!-- Contenuto principale demo -->
            <v-col cols="12" md="10" class="demo-main pa-4">

                <!-- Anteprima elenco commesse (solo visivo, fake) -->
                <div class="demo-section mb-6">
                    <div class="demo-section-header">
                        <v-icon size="18" color="primary" class="me-2">mdi-clipboard-list-outline</v-icon>
                        <span class="font-weight-bold">Commesse</span>
                        <v-chip size="x-small" color="info" variant="tonal" class="ms-2">Anteprima</v-chip>
                    </div>

                    <!-- Tabella fake commesse -->
                    <v-card class="mt-2 elevation-1" rounded="lg">
                        <v-data-table :headers="headersFake" :items="commesseFake" hide-default-footer density="compact"
                            class="demo-table">
                            <template v-slot:[`item.stato`]="{ item }">
                                <v-chip :color="item.statoColor" size="x-small" variant="tonal">{{ item.stato
                                    }}</v-chip>
                            </template>
                        </v-data-table>
                    </v-card>
                </div>

                <!-- Anteprima form (sfocato) -->
                <div class="demo-section">
                    <div class="demo-section-header mb-2">
                        <v-icon size="18" color="primary" class="me-2">mdi-plus-circle-outline</v-icon>
                        <span class="font-weight-bold">Nuova commessa</span>
                        <v-chip size="x-small" color="info" variant="tonal" class="ms-2">Anteprima</v-chip>
                    </div>

                    <div class="form-blur-wrap">
                        <!-- Form visivo sfocato -->
                        <v-card class="elevation-1 form-blur" rounded="lg">
                            <v-card-title class="pa-3 text-body-2 font-weight-bold">Dati ordine</v-card-title>
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="6"><v-text-field label="N. Commessa" variant="outlined"
                                            density="compact" model-value="2025/001" readonly /></v-col>
                                    <v-col cols="6"><v-text-field label="Data" variant="outlined" density="compact"
                                            model-value="15/01/2025" readonly /></v-col>
                                    <v-col cols="6"><v-text-field label="Ragione Sociale" variant="outlined"
                                            density="compact" model-value="Esempio S.r.l." readonly /></v-col>
                                    <v-col cols="6"><v-text-field label="Città" variant="outlined" density="compact"
                                            model-value="Milano" readonly /></v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <!-- Overlay blocco -->
                        <div class="blur-overlay">
                            <v-icon size="48" color="white" class="mb-3">mdi-lock-outline</v-icon>
                            <div class="text-h6 text-white font-weight-bold mb-1">Funzionalità bloccata</div>
                            <div class="text-body-2 text-white text-center" style="opacity:.85">
                                Attiva la licenza per iniziare a caricare i rilievi
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Call to action centrale -->
                <v-card class="cta-card mt-6 elevation-2" rounded="lg">
                    <v-card-text class="d-flex flex-column flex-md-row align-center gap-4 pa-5">
                        <div class="flex-1">
                            <div class="text-h6 font-weight-bold mb-1" style="color:#1E3A5F">
                                {{ ctaTitolo }}
                            </div>
                            <div class="text-body-2 text-medium-emphasis">{{ ctaTesto }}</div>
                        </div>
                        <v-btn color="primary" size="large" variant="elevated" prepend-icon="mdi-email-outline"
                            href="mailto:info@example.com?subject=Richiesta attivazione licenza">
                            Contatta il supporto
                        </v-btn>
                    </v-card-text>
                </v-card>

            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import LicenzaBanner from '@/components/LicenzaBanner.vue';

export default {
    name: 'PaginaDemo',

    components: { LicenzaBanner },

    setup() {
        const store = useStore()

        // Questi valori vanno letti dal token JWT / store utente
        // Adatta in base a come li esponi nel tuo store
        const licenzaStato = computed(() => store.getters['user/getLicenzaStato'] ?? 0)
        const emailConfirmata = computed(() => store.getters['user/getEmailConfirmata'] ?? false)

        const menuDemo = [
            { label: 'Commesse', icon: 'mdi-clipboard-list-outline', locked: false },
            { label: 'Nuova commessa', icon: 'mdi-plus-circle-outline', locked: true },
            { label: 'Gestione utenti', icon: 'mdi-account-group', locked: true },
            { label: 'Parametri', icon: 'mdi-tune-variant', locked: true },
            { label: 'Profilo', icon: 'mdi-account-cog', locked: true },
        ]

        const headersFake = [
            { title: 'N. Commessa', value: 'numero', width: '130' },
            { title: 'Cliente', value: 'cliente' },
            { title: 'Cantiere', value: 'cantiere' },
            { title: 'Data', value: 'data', width: '110' },
            { title: 'Stato', value: 'stato', width: '100' },
        ]

        const commesseFake = [
            { numero: '2025/001', cliente: 'Rossi Mario', cantiere: 'Via Roma 10, Milano', data: '10/01/2025', stato: 'Completata', statoColor: 'success' },
            { numero: '2025/002', cliente: 'Verdi S.r.l.', cantiere: 'Via Torino 5, Torino', data: '12/01/2025', stato: 'In corso', statoColor: 'primary' },
            { numero: '2025/003', cliente: 'Bianchi Anna', cantiere: 'Via Napoli 3, Napoli', data: '14/01/2025', stato: 'Bozza', statoColor: 'warning' },
        ]

        const ctaTitolo = computed(() => {
            if (licenzaStato.value === -1) return 'Account sospeso'
            if (!emailConfirmata.value) return 'Conferma la tua email'
            return 'In attesa di attivazione'
        })

        const ctaTesto = computed(() => {
            if (licenzaStato.value === -1)
                return 'Il tuo account è stato sospeso. Contatta il supporto per ulteriori informazioni.'
            if (!emailConfirmata.value)
                return 'Controlla la tua casella di posta e clicca sul link di verifica per proseguire.'
            return 'La tua registrazione è stata ricevuta. Il nostro team la verificherà a breve e riceverai una email di conferma.'
        })

        const reinviaEmail = () => {
            // TODO: chiamata API per reinvio email conferma
            console.log('Reinvio email di conferma')
        }

        return {
            licenzaStato, emailConfirmata,
            menuDemo, headersFake, commesseFake,
            ctaTitolo, ctaTesto, reinviaEmail
        }
    }
}
</script>

<style scoped>
.demo-container {
    background: #F3F4F6;
    min-height: 100vh;
    flex-direction: column;
}

/* Sidebar demo */
.demo-sidebar {
    background: white;
    border-right: 1px solid #eef0f3;
    min-height: calc(100vh - 48px);
}

.sidebar-logo {
    padding: 8px 4px;
    border-bottom: 1px solid #eef0f3;
}

.sidebar-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-radius: 8px;
    margin-bottom: 2px;
    font-size: 13px;
    color: #374151;
    transition: background .15s;
}

.sidebar-item:not(.locked):hover {
    background: #f0f4f8;
    cursor: pointer;
}

.sidebar-item.locked {
    opacity: .45;
    cursor: not-allowed;
}

/* Sezioni demo */
.demo-section-header {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

/* Form sfocata */
.form-blur-wrap {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
}

.form-blur {
    filter: blur(3px);
    pointer-events: none;
    user-select: none;
}

.blur-overlay {
    position: absolute;
    inset: 0;
    background: rgba(30, 58, 95, 0.65);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    backdrop-filter: blur(2px);
}

/* Tabella demo */
.demo-table :deep(tbody tr) {
    pointer-events: none;
}

/* CTA card */
.cta-card {
    border: 1px solid rgba(30, 58, 95, .12);
    background: linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%);
}
</style>